import { Box, HStack, IconButton, Input, InputProps } from '@chakra-ui/react';
import { ChangeEvent, forwardRef, ReactNode, useId } from 'react';
import { useIntl } from 'react-intl';
import { X } from 'lucide-react';
import {
  B2B_FIELD_CONTROL_BORDER_RADIUS,
  B2B_FIELD_CONTROL_BORDER_WIDTH,
  B2B_FIELD_CONTROL_HEIGHT_PX,
  B2B_FIELD_CONTROL_MAX_WIDTH,
  B2B_FIELD_CONTROL_PADDING_X,
  B2B_FIELD_CONTROL_PADDING_Y,
} from '../b2bFormControlTokens';
import { B2bFieldLayout } from '../B2bFieldLayout';
import { b2bFieldFocusVisible } from 'styles/colors';

export type FieldDateProps = Omit<
  InputProps,
  'id' | 'type' | 'value' | 'defaultValue' | 'onChange'
> & {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /**
   * Local calendar date as **`YYYY-MM-DD`** (native `input[type=date]` value). `undefined` when unset —
   * prefer omit / `undefined` in form state over `""` so optional fields drop out of JSON.
   */
  value?: string;
  onValueChange: (value: string | undefined) => void;
  /** When true, a clear control sets `undefined` (omittable optional). */
  isOptional?: boolean;
  layoutDir?: 'ltr' | 'rtl';
};

const inputChrome = {
  textAlign: 'start' as const,
  borderRadius: B2B_FIELD_CONTROL_BORDER_RADIUS,
  borderWidth: B2B_FIELD_CONTROL_BORDER_WIDTH,
  borderColor: 'dark.D150',
  _hover: { borderColor: 'dark.D200' },
  _focusVisible: {
    ...b2bFieldFocusVisible,
  },
};

/**
 * Native date input. Upstream wire format: **`YYYY-MM-DD`** (not full ISO datetime).
 */
export const FieldDate = forwardRef<HTMLInputElement, FieldDateProps>(
  (
    {
      id: idProp,
      label,
      hint,
      error,
      value,
      onValueChange,
      isOptional = false,
      isDisabled,
      isRequired,
      isReadOnly,
      isInvalid,
      ...restInputProps
    },
    ref,
  ) => {
    const intl = useIntl();
    const autoId = useId();
    const fieldId = idProp ?? `b2b-field-date-${autoId}`;
    const clearLabel = intl.formatMessage({
      id: 'b2b.registration.field.clearOptional',
      defaultMessage: 'Clear',
    });

    /** Date value is always Western numerals / `YYYY-MM-DD` — keep LTR typing and cursor behavior in RTL forms. */
    const placeholderSx = { color: 'dark.D400', textAlign: 'left' as const };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      onValueChange(next === '' ? undefined : next);
    };

    const handleClear = () => {
      onValueChange(undefined);
    };

    return (
      <B2bFieldLayout
        fieldId={fieldId}
        label={label}
        hint={hint}
        error={error}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isReadOnly={isReadOnly}
        isInvalid={isInvalid}
      >
        <HStack align='center' spacing={2} w='full' maxW={B2B_FIELD_CONTROL_MAX_WIDTH}>
          <Box flex={1} minW={0}>
            <Input
              id={fieldId}
              ref={ref}
              type='date'
              value={value ?? ''}
              onChange={handleChange}
              dir='ltr'
              w='full'
              maxW='100%'
              display='block'
              h={B2B_FIELD_CONTROL_HEIGHT_PX}
              minH={B2B_FIELD_CONTROL_HEIGHT_PX}
              px={B2B_FIELD_CONTROL_PADDING_X}
              py={B2B_FIELD_CONTROL_PADDING_Y}
              fontSize='md'
              lineHeight='1.25'
              _placeholder={placeholderSx}
              {...inputChrome}
              textAlign='left'
              {...restInputProps}
            />
          </Box>
          {isOptional && value != null && value !== '' ? (
            <IconButton
              type='button'
              aria-label={clearLabel}
              variant='ghost'
              size='md'
              flexShrink={0}
              icon={<X size={18} aria-hidden />}
              onClick={handleClear}
              isDisabled={isDisabled || isReadOnly}
            />
          ) : null}
        </HStack>
      </B2bFieldLayout>
    );
  },
);

FieldDate.displayName = 'FieldDate';
